import { useMaxWidthWrapperScrollElement } from '@components/layout/MaxWidthWrapper';
import { useInfiniteLazyLoader } from '@hooks/useInfiniteLazyLoader';
import { useMediaQuery } from '@mui/material';
import { useLazyGetPublicNewsQuery } from '@news/services';
import { NewsItem } from '@news/types';
import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

export const useOverviewNews = () => {
    const [searchParams] = useSearchParams();
    const selectedTab = searchParams.get('tab') || 'latest';

    const isLg = useMediaQuery((theme) => theme.breakpoints.up('lg'));
    const isMd = useMediaQuery((theme) => theme.breakpoints.up('md'));
    const isSm = useMediaQuery((theme) => theme.breakpoints.up('sm'));
    const pageSize = isLg ? 16 : isMd ? 12 : isSm ? 8 : 4;

    const scrollElement = useMaxWidthWrapperScrollElement();

    const [getNews, { isError: isGetNewsError }] = useLazyGetPublicNewsQuery();

    const loadNews = useCallback(
        async ({ page, pageSize, trending }: { page: number; pageSize: number; trending?: boolean }) => {
            const response = await getNews(
                {
                    page,
                    page_size: pageSize,
                    top_last_3_months: trending,
                },
                true
            ).unwrap();

            return response;
        },
        [getNews]
    );

    const {
        items: latestNews,
        sentinelRef: latestNewsSentinelRef,
        hasRealItems: hasLatestNewsRealItems,
        isLoadingMore: isLoadingMoreLatestNews,
    } = useInfiniteLazyLoader<NewsItem>({
        pageSize,
        scrollElement,
        contextKey: `latest-news:${pageSize}`,
        loadPage: loadNews,
        getItemKey: (newsItem) => newsItem.id,
        onError: (error) => {
            console.error('Error loading latest news:', error);
        },
        enabled: selectedTab === 'latest',
        scrollToTopOnReset: false,
    });

    const {
        items: trendingNews,
        sentinelRef: trendingNewsSentinelRef,
        hasRealItems: hasTrendingNewsRealItems,
        isLoadingMore: isLoadingMoreTrendingNews,
    } = useInfiniteLazyLoader<NewsItem>({
        pageSize,
        scrollElement,
        contextKey: `trending-news:${pageSize}`,
        loadPage: (params) => loadNews({ ...params, trending: true }),
        getItemKey: (newsItem) => newsItem.id,
        onError: (error) => {
            console.error('Error loading trending news:', error);
        },
        enabled: selectedTab === 'trending',
        scrollToTopOnReset: false,
    });

    return {
        latest: {
            news: latestNews,
            sentinelRef: latestNewsSentinelRef,
            hasRealItems: hasLatestNewsRealItems,
            isLoadingMore: isLoadingMoreLatestNews,
        },
        trending: {
            news: trendingNews,
            sentinelRef: trendingNewsSentinelRef,
            hasRealItems: hasTrendingNewsRealItems,
            isLoadingMore: isLoadingMoreTrendingNews,
        },
        isGetNewsError,
    };
};
